import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { ShieldCheck, ExternalLink, Award, Maximize2 } from "lucide-react";

export default function Recognitions() {
  const [items, setItems] = useState([]);
  const [zoom, setZoom] = useState(null);

  useEffect(() => { api.get("/recognitions").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <div data-testid="recognitions-page">
      <PageHero
        eyebrow="Federation Recognitions"
        title="Officially Recognised by Global Karate Bodies"
        subtitle="The National Karate Federation – NKF Bharat is officially affiliated with and recognised by international and national karate governing bodies. Each recognition certificate is on display below."
      />

      <section className="section-spacing nkf-container">
        {items.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-10 text-center">
            <ShieldCheck className="h-10 w-10 text-slate-400 mx-auto" />
            <div className="font-display text-xl text-nkf-blue mt-3">Recognition certificates will be displayed here</div>
            <p className="text-sm text-slate-500 mt-2">The federation administrator will publish official recognition certificates as soon as they are uploaded.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {items.map((r) => (
              <article key={r.id} data-testid={`recognition-${r.id}`} className="federation-card group bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col h-full">
                <button
                  type="button"
                  onClick={() => setZoom(r)}
                  className="relative block aspect-[4/3] bg-slate-100 overflow-hidden w-full"
                >
                  <img src={r.certificate_url} alt={r.body_name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-nkf-navy/85 via-nkf-navy/0 opacity-0 group-hover:opacity-100 transition flex items-end justify-between p-3">
                    <span className="text-[10px] uppercase tracking-widest text-white/90">Click to enlarge</span>
                    <Maximize2 className="h-4 w-4 text-white" />
                  </div>
                </button>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-3">
                    {r.logo_url && (
                      <img src={r.logo_url} alt="" className="h-9 w-9 rounded-full object-contain bg-slate-50 border border-slate-200 p-1" />
                    )}
                    <div className="min-w-0">
                      <div className="font-display text-lg text-nkf-blue truncate">{r.body_name}</div>
                      {r.short_code && <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold">{r.short_code}</div>}
                    </div>
                  </div>
                  {r.description && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{r.description}</p>}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[12px]">
                    <div className="flex items-center gap-1.5 text-slate-500">
                      <Award className="h-3.5 w-3.5 text-nkf-gold" />
                      {r.issued_year ? `Recognised ${r.issued_year}` : "Officially Recognised"}
                    </div>
                    {r.link && (
                      <a href={r.link} target="_blank" rel="noreferrer" className="text-nkf-blue hover:text-nkf-red font-semibold inline-flex items-center gap-1">
                        Visit <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-12 p-6 md:p-8 bg-nkf-navy text-white rounded-md flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-7 w-7 text-nkf-gold shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Federation Recognition</div>
              <div className="font-display text-2xl mt-1">Backed by global karate governing bodies</div>
              <div className="text-white/70 text-sm mt-1">Click any document above to enlarge. For affiliation or recognition queries, contact the federation secretariat.</div>
            </div>
          </div>
          <a href="/contact" className="bg-nkf-gold text-nkf-navy px-5 py-2.5 rounded text-sm uppercase tracking-widest font-bold hover:bg-white transition">Contact Federation</a>
        </div>
      </section>

      {/* Lightbox */}
      {zoom && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 bg-nkf-navy/90 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
          data-testid="recognition-lightbox"
        >
          <div className="relative max-w-5xl w-full max-h-[90vh] flex flex-col animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setZoom(null)} className="self-end mb-3 text-white/80 hover:text-nkf-gold text-sm uppercase tracking-widest">Close ✕</button>
            <img src={zoom.certificate_url} alt={zoom.body_name} className="w-full max-h-[80vh] object-contain bg-white rounded-md" />
            <div className="mt-3 text-center text-white/90">
              <div className="font-display text-xl">{zoom.body_name}</div>
              {zoom.description && <div className="text-sm text-white/70 mt-1 max-w-2xl mx-auto">{zoom.description}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
